import { captureEvent } from './telemetry/posthog/frontend';

/**
 * Detects errors indicating the MediaDevices API is unavailable entirely.
 * Returns `true` for Menlo Security RBI environments (NotSupportedError)
 * and sandboxed iframes where `navigator.mediaDevices` is undefined (TypeError).
 */
export function isMediaUnsupportedError(error: unknown): boolean {
  if (error instanceof DOMException && error.name === 'NotSupportedError') {
    return true;
  }
  if (error instanceof TypeError && /mediaDevices/i.test(error.message)) {
    return true;
  }
  return false;
}

export interface MediaStreamResult {
  stream: MediaStream;
  fallbackUsed: boolean;
  requestedDeviceId?: string;
  actualDeviceId?: string;
}

interface GetMediaStreamOptions {
  deviceId?: string;
  kind: 'audio' | 'video';
  constraints?: MediaTrackConstraints;
  context?: string;
}

/**
 * Requests a media stream with automatic fallback when the requested device
 * is unavailable. On OverconstrainedError or NotFoundError (common when a
 * Bluetooth device is disconnected or a stale device ID is used), retries
 * without a device constraint so the browser picks the system default.
 *
 * All other errors (NotAllowedError, etc.) propagate unchanged.
 */
export async function getMediaStreamWithFallback(
  options: GetMediaStreamOptions
): Promise<MediaStreamResult> {
  const { deviceId, kind, constraints = {}, context = '' } = options;

  const buildConstraints = (id?: string): MediaStreamConstraints => {
    const trackConstraints: MediaTrackConstraints = {
      ...constraints,
      ...(id ? { deviceId: { exact: id } } : {}),
    };

    return kind === 'audio'
      ? { audio: trackConstraints }
      : { video: trackConstraints };
  };

  // If no deviceId, request with no device constraint directly
  if (!deviceId) {
    const stream = await navigator.mediaDevices.getUserMedia(
      buildConstraints()
    );
    const tracks = kind === 'audio' ? stream.getAudioTracks() : stream.getVideoTracks();
    const actualDeviceId = tracks[0]?.getSettings().deviceId;
    return { stream, fallbackUsed: false, actualDeviceId };
  }

  // Try exact device first
  try {
    const stream = await navigator.mediaDevices.getUserMedia(
      buildConstraints(deviceId)
    );
    const tracks = kind === 'audio' ? stream.getAudioTracks() : stream.getVideoTracks();
    const actualDeviceId = tracks[0]?.getSettings().deviceId;
    return { stream, fallbackUsed: false, requestedDeviceId: deviceId, actualDeviceId };
  } catch (error: unknown) {
    // Only fall back for device-unavailable errors
    if (
      error instanceof DOMException &&
      (error.name === 'OverconstrainedError' || error.name === 'NotFoundError')
    ) {
      console.warn(
        `[media-utils] ${error.name} for device ${deviceId} (${context}), falling back to default`
      );

      // Retry without device constraint
      const stream = await navigator.mediaDevices.getUserMedia(
        buildConstraints()
      );
      const tracks = kind === 'audio' ? stream.getAudioTracks() : stream.getVideoTracks();
      const actualDeviceId = tracks[0]?.getSettings().deviceId;

      captureEvent('device_fallback_triggered', {
        requestedDeviceId: deviceId,
        actualDeviceId: actualDeviceId || 'unknown',
        errorName: error.name,
        kind,
        context,
      });

      return {
        stream,
        fallbackUsed: true,
        requestedDeviceId: deviceId,
        actualDeviceId,
      };
    }

    // All other errors propagate unchanged
    throw error;
  }
}
